import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  SECESSION_REASON_MAX_LENGTH,
  USER_STATUS,
  USER_STATUS_OPTIONS,
  userStatusChangeMessage,
  userStatusLabel,
  userStatusTone,
} from '@/utils/userStatus';

const normalizeStatus = (status) => {
  if (Object.values(USER_STATUS).includes(status)) {
    return status;
  }
  return USER_STATUS.INACTIVE;
};

const StatusDot = ({ status }) => {
  const tone = userStatusTone(status);
  return (
    <Box
      component="span"
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        backgroundColor: tone.color,
        flexShrink: 0,
      }}
    />
  );
};

const StatusLabel = ({ status }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      minWidth: 0,
    }}
  >
    <StatusDot status={status} />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {userStatusLabel(status)}
    </Box>
  </Box>
);

const UserStatusCell = ({ row, onStatusChange }) => {
  const currentStatus = normalizeStatus(row.status);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [secessionReason, setSecessionReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  const confirmOpen = pendingStatus != null;
  const isSecessionPending = pendingStatus === USER_STATUS.SECESSION;
  const confirmMessage = useMemo(
    () => userStatusChangeMessage(currentStatus, pendingStatus),
    [currentStatus, pendingStatus]
  );

  const resetDialog = () => {
    setPendingStatus(null);
    setSecessionReason('');
    setReasonError(false);
  };

  const handleSelectChange = (event) => {
    event.stopPropagation();
    const nextStatus = event.target.value;
    if (nextStatus === currentStatus) {
      return;
    }
    setSecessionReason('');
    setReasonError(false);
    setPendingStatus(nextStatus);
  };

  const handleCancel = () => resetDialog();

  const handleConfirm = () => {
    if (!pendingStatus) {
      return;
    }
    if (isSecessionPending && !secessionReason.trim()) {
      setReasonError(true);
      return;
    }
    onStatusChange(
      row.userId,
      pendingStatus,
      isSecessionPending ? secessionReason.trim() : undefined
    );
    resetDialog();
  };

  const tone = userStatusTone(currentStatus);

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}
    >
      <Tooltip
        title={currentStatus === USER_STATUS.SECESSION && row.secessionOpinion ? row.secessionOpinion : ''}
        disableHoverListener={!(currentStatus === USER_STATUS.SECESSION && row.secessionOpinion)}
      >
        <Box>
          <Select
        size="small"
        value={currentStatus}
        onChange={handleSelectChange}
        onClick={(e) => e.stopPropagation()}
        IconComponent={KeyboardArrowDownRoundedIcon}
        renderValue={(value) => <StatusLabel status={value} />}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.75,
              px: 0.75,
              py: 0.5,
              borderRadius: 2.5,
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.14)',
              overflow: 'visible',
            },
          },
          MenuListProps: {
            sx: { py: 0.25, display: 'flex', flexDirection: 'column', gap: 0.5 },
          },
        }}
        sx={{
          minWidth: 124,
          height: 30,
          borderRadius: 999,
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: '-0.02em',
          color: tone.color,
          backgroundColor: tone.bg,
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            backgroundColor: tone.hover,
          },
          '& .MuiSelect-select': {
            py: '4px',
            pl: 1.25,
            pr: '30px !important',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
          },
          '& .MuiSelect-icon': {
            color: tone.color,
            right: 6,
            fontSize: 18,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 999,
            borderColor: tone.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tone.color,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tone.color,
            borderWidth: 1,
          },
        }}
      >
        {USER_STATUS_OPTIONS.map((option) => {
          const optionTone = userStatusTone(option.value);
          const selected = option.value === currentStatus;

          return (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                mx: 0,
                minHeight: 30,
                borderRadius: 999,
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: '-0.02em',
                color: optionTone.color,
                backgroundColor: selected ? optionTone.hover : optionTone.bg,
                border: `1px solid ${optionTone.border}`,
                '&:hover': {
                  backgroundColor: optionTone.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: optionTone.hover,
                  '&:hover': {
                    backgroundColor: optionTone.hover,
                  },
                },
              }}
            >
              <StatusLabel status={option.value} />
            </MenuItem>
          );
        })}
          </Select>
        </Box>
      </Tooltip>

      <Dialog
        open={confirmOpen}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>상태 변경</DialogTitle>
        <DialogContent sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#333' }}>
          {confirmMessage}
          {isSecessionPending && (
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              label="계정 잠금 사유"
              placeholder="계정 잠금 사유를 입력해 주세요."
              value={secessionReason}
              onChange={(event) => {
                setSecessionReason(event.target.value);
                if (reasonError && event.target.value.trim()) {
                  setReasonError(false);
                }
              }}
              error={reasonError}
              helperText={reasonError ? '계정 잠금 사유를 입력해 주세요.' : `${secessionReason.length}/${SECESSION_REASON_MAX_LENGTH}`}
              inputProps={{ maxLength: SECESSION_REASON_MAX_LENGTH }}
              sx={{
                mt: 2,
                '& .MuiInputBase-root': { fontWeight: 500, fontSize: '0.95rem' },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>취소</Button>
          <Button
            onClick={handleConfirm}
            autoFocus={!isSecessionPending}
            disabled={isSecessionPending && !secessionReason.trim()}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserStatusCell;
